import { isAvailableBookingRange } from "./booking-slots";
import { BookingDatePicker } from "./booking-date-picker";
import { Root as PeriodSelect } from "@radix-ui/react-select";
import { useFormik } from "formik";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  MultiSelect,
  Label,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  MoneyInput,
  Textarea,
  Option,
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import {
  useAuth,
  useCreatePatientJustNameMutation,
  useCreateSchedulingMutation,
  useFetchEmployeesNoPaginateQuery,
  useFetchPatientsQuery,
  useFetchProceduresQuery,
  useFetchTimeSchedulingsQuery,
  useToast,
} from "@/hooks";
import {
  CreateScheduleRequestDTO,
  Patient,
  PatientsResponse,
  ProcedureResponse,
  TimeSchedulingResponse,
} from "@/types";
import { DisplayError, formatDateToBackend, removeLastTwoChars } from "@/utils";

import { MessageError } from "../../message-error";
import { SchedulingsValidationSchema } from "@/shared";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { httpRequest } from "@/lib";
import { TriangleAlert } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { hasFeature } from "@/helpers";

type Props = {
  handleClose: () => void;
  defaultDate?: string;
  defaultStartTime?: string;
  defaultEndTime?: string;
  patientId?: string;
};

export function FormCreateScheduling({
  handleClose,
  defaultDate,
  defaultEndTime,
  defaultStartTime,
  patientId
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const { user } = useAuth();
  const googleCalendarIncluded = hasFeature(user, "google_calendar");

  const queryClient = useQueryClient();

  const [createdPatient, setCreatedPatient] = useState<Patient>();
  const [selectedProcedure, setSelectedProcedure] = useState<
    Option | undefined
  >();

  const { data: employeesData, isLoading: employeesLoading } =
    useFetchEmployeesNoPaginateQuery();
  const { data: patientsData, isLoading: patientsLoading } =
    useFetchPatientsQuery({
      page: "1",
      per_page: 10,
      search: "",
    });
  const { data: proceduresData, isLoading: proceduresLoading } =
    useFetchProceduresQuery({ page: "1", perPage: "10", search: "" });
  const {
    mutateAsync: mutateAsyncCreatepatient,
    isLoading: isLoadingCreatePatient,
  } = useCreatePatientJustNameMutation();
  const { mutateAsync, isLoading } = useCreateSchedulingMutation();

  const allowDefaultTimes = useRef(true);

  const initialValues = {
    patientsId: patientId || queryParams.get("patient") || "",
    employeesId: queryParams.get("collaborator") || "",
    proceduresId: queryParams.get("procedure") || "",
    date: defaultDate || "",
    syncWithGoogle:
      user?.notificationsSettings?.google_calendar[
      "Automatic synchronization"
      ] || false,
    confirmWhatsApp: false,
    value: Number(queryParams.get("value")).toFixed(2) || "",
    return: 0,
    comments: "",
    hour: [] as string[],
    period: "",
    queue: "no send",
    whatsapp: false,
    sms: false,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: SchedulingsValidationSchema(),
    onSubmit: async (schedulingData) => {
      handleCreateScheduleSubmit(schedulingData);
    },
  });

  const { data: availableTimes, isLoading: availableTimesLoading, isError: availableTimesError } =
    useFetchTimeSchedulingsQuery({
      employeesId: formik.values.employeesId,
      date: formatDateToBackend(formik.values.date),
    });

  const hours = !availableTimesLoading && !availableTimesError && formik.values.employeesId && formik.values.date
    ? availableTimes : undefined;
  const periodHours = hours?.[formik.values.period as keyof TimeSchedulingResponse] ?? [];
  const invalidRange = formik.values.hour.length > 0 && !isAvailableBookingRange(formik.values.hour, periodHours);

  function validateTimeRange(
    hour: string[],
    defaultStartTime?: string,
    defaultEndTime?: string
  ): string[] {
    const startTime = removeLastTwoChars(defaultStartTime);
    const endTime = removeLastTwoChars(defaultEndTime);
    if (startTime && endTime && isAvailableBookingRange([startTime, endTime], hour)) {
      return [startTime, endTime].sort();
    }
    return [];
  }
  async function handleCreateScheduleSubmit(
    schedulingData: CreateScheduleRequestDTO
  ) {
    if (availableTimesLoading || availableTimesError || !isAvailableBookingRange(schedulingData.hour, periodHours)) {
      formik.setFieldError("hour", "Selecione horários consecutivos e disponíveis.");
      formik.setSubmitting(false);
      return;
    }
    try {
      const request = {
        ...schedulingData,
        date: formatDateToBackend(schedulingData.date),
      };
      await mutateAsync(request, {
        onSuccess: () => {
          formik.resetForm();
          handleClose();
          if (queryParams.get("openForm")) {
            removeQueryParams();
          }
          toast({
            title: "Sucesso!",
            description: "Agendamento criado com sucesso",
            variant: "default",
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    } finally {
      formik.setSubmitting(false);
    }
  }

  const handleSearchPatients = async (term: string) => {
    try {
      const { data } = await httpRequest.get<PatientsResponse>(`/patients`, {
        params: { search: term },
      });
      queryClient.setQueryData(["patients", { search: term }], data);

      return (
        data?.items.map((patient) => ({
          value: patient.id,
          label: patient.person.name,
        })) || []
      );
    } catch {
      return [];
    }
  };

  const handleSearchProcedures = async (term: string) => {
    try {
      const { data } = await httpRequest.get<ProcedureResponse>(`/procedures`, {
        params: { search: term },
      });
      queryClient.setQueryData(["procedures", { search: term }], data);

      return (
        data?.items.map((procedure) => ({
          value: procedure.id,
          label: procedure.name,
        })) || []
      );
    } catch {
      return [];
    }
  };

  function removeQueryParams() {
    queryParams.delete("patient");
    queryParams.delete("collaborator");
    queryParams.delete("procedure");
    queryParams.delete("budget");
    queryParams.delete("value");
    queryParams.delete("openForm");
    navigate(`${location.pathname}?${queryParams.toString()}`);
  }

  const { setFieldValue } = formik;
  useEffect(() => {
    // A refreshed response can remove a selected slot even without an input change.
    if (!hours && formik.values.hour.length > 0) {
      void setFieldValue("hour", []);
    }
  }, [hours, formik.values.hour.length, setFieldValue]);

  const selectedPatientId = formik.values.patientsId;
  const knownPatient = patientsData?.items.find((patient) => patient.id === selectedPatientId)
    ?? (createdPatient?.id === selectedPatientId ? createdPatient : undefined);
  const { data: fetchedPatient, isError: selectedPatientError } = useQuery({
    queryKey: ["patient", selectedPatientId],
    queryFn: async () => {
      const { data } = await httpRequest.get<Patient>(
        `/patients/${selectedPatientId}`
      );
      return data;
    },
    enabled: !!selectedPatientId && !knownPatient,
    keepPreviousData: false,
  });
  // Derive context from the active ID; a late response only updates its own query.
  const currentPatient = knownPatient
    ?? (fetchedPatient?.id === selectedPatientId ? fetchedPatient : undefined);
  const selectedPatient: Option | undefined = currentPatient
    ? { value: currentPatient.id, label: currentPatient.person.name }
    : undefined;
  const isOverdueBills = currentPatient?.is_overdue_bills ?? false;

  useQuery({
    queryKey: ["procedures", formik.values.proceduresId],
    queryFn: async () => {
      if (!formik.values.proceduresId) return null;
      const { data } = await httpRequest.get(
        `/procedures/${formik.values.proceduresId}`
      );
      return data;
    },
    enabled: !!formik.values.proceduresId && !selectedProcedure,
    onSuccess: (data) => {
      if (data) {
        setSelectedProcedure({
          value: data.id,
          label: data.name,
        });
      }
    },
  });

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="flex min-h-0 max-h-[calc(100dvh-2rem)] flex-col"
    >
      <div className="flex min-h-0 flex-col">
        <DialogHeader className="shrink-0 border-b border-app-border-subtle px-5 py-4 pr-12 text-left sm:px-6">
          <DialogTitle className="font-display text-2xl text-app-fg-strong">
            Novo agendamento
          </DialogTitle>
          <DialogDescription>
            Escolha o atendimento e um horário disponível.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 sm:px-6 space-y-4">
            {isOverdueBills && (
              <div className="max-w-sm mx-auto">
                <Alert className="border-app-warning bg-app-warning/10 text-app-fg">
                  <TriangleAlert className="size-5 text-app-warning" />
                  <AlertTitle>Pendência financeira</AlertTitle>
                  <AlertDescription className="space-y-1 text-sm text-app-fg mt-3">
                    <p>
                      O Paciente selecionado possui valores em aberto. Para mais
                      informações,{" "}
                      <Link to={`/patients?patient=${selectedPatient?.value}`}>
                        clique aqui.
                      </Link>
                    </p>
                  </AlertDescription>
                </Alert>
              </div>
            )}
          <h3 className="font-display text-lg text-app-fg-strong">Paciente e atendimento</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label required={!patientId}>{selectedPatient ? "Agendamento para" : t("LABEL.patient")}</Label>
              {patientId ? (
                selectedPatient && <p className="flex min-h-10 items-center break-words font-display text-lg text-app-fg-strong">{selectedPatient.label}</p>
              ) : (
              <div className="space-y-1">
                <MultiSelect
                  options={
                    patientsData?.items.map((patient) => ({
                      value: patient.id,
                      label: patient.person.name,
                    })) || []
                  }
                  loading={patientsLoading || isLoadingCreatePatient}
                  placeholder="Selecione um paciente..."
                  isInvalid={
                    formik.touched.patientsId && formik.errors.patientsId
                  }
                  isValid={
                    formik.touched.patientsId && !formik.errors.patientsId
                  }
                  value={formik.values.patientsId}
                  selectedOptions={selectedPatient ? [selectedPatient] : []}
                  onChange={(value) => formik.setFieldValue("patientsId", value)}
                  onBlur={() => formik.handleBlur("patientsId")}
                  messageError={formik.errors.patientsId}
                  onSearch={handleSearchPatients}
                  allowCreate
                  onCreateOption={async (inputValue) => {
                    try {
                      let newOption: Option = { value: "", label: "" };
                      await mutateAsyncCreatepatient(
                        { name: inputValue },
                        {
                          onSuccess: ({ data }) => {
                            setCreatedPatient(data);
                            newOption = {
                              value: data.id,
                              label: data.person.name,
                            };
                          },
                          onError: (error) => {
                            toast({
                              title: t("LABEL.error"),
                              description: DisplayError(
                                error,
                                t("MESSAGE.error_message")
                              ),
                              variant: "destructive",
                            });
                          },
                        }
                      );

                      if (!newOption.value) {
                        throw new Error("Falha ao criar novo paciente");
                      }

                      return newOption;
                    } catch (error) {
                      toast({
                        title: t("LABEL.error"),
                        description: DisplayError(
                          error,
                          t("MESSAGE.error_message")
                        ),
                        variant: "destructive",
                      });
                      throw error;
                    }
                  }}
                />
              </div>
              )}
          {selectedPatientId && !selectedPatient && (
            <p role={selectedPatientError ? "alert" : "status"} className="text-sm text-app-fg">
              {selectedPatientError
                ? "Não foi possível carregar o paciente selecionado. Selecione-o novamente para tentar outra consulta."
                : "Carregando paciente selecionado…"}
            </p>
          )}
            </div>
            <div className="flex flex-col gap-2">
              <Label required>{t("LABEL.collaborator")}</Label>
              <div className="space-y-1">
                <MultiSelect
                  options={
                    employeesData?.map((employee) => ({
                      value: employee.id,
                      label: employee.person.name,
                    })) || []
                  }
                  loading={employeesLoading}
                  placeholder="Selecione um colaborador..."
                  isInvalid={
                    formik.touched.employeesId && formik.errors.employeesId
                  }
                  isValid={
                    formik.touched.employeesId && !formik.errors.employeesId
                  }
                  value={formik.values.employeesId}
                  onChange={(value) => {
                    if (formik.values.employeesId) allowDefaultTimes.current = false;
                    formik.setValues({ ...formik.values, employeesId: String(value), period: "", hour: [] });
                  }}
                  onBlur={() => formik.handleBlur("employeesId")}
                  messageError={formik.errors.employeesId}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label required>{t("LABEL.procedure")}</Label>
              <div className="space-y-1">
                <MultiSelect
                  options={
                    proceduresData?.items.map((procedure) => ({
                      value: procedure.id,
                      label: procedure.name,
                    })) || []
                  }
                  loading={proceduresLoading}
                  isInvalid={
                    formik.touched.proceduresId && formik.errors.proceduresId
                  }
                  isValid={
                    formik.touched.proceduresId && !formik.errors.proceduresId
                  }
                  selectedOptions={selectedProcedure ? [selectedProcedure] : []}
                  placeholder="Selecione um procedimento..."
                  emptyMessage="Nenhum procedimento encontrado"
                  value={formik.values.proceduresId}
                  onSearch={handleSearchProcedures}
                  onChange={(value) => {
                    formik.setFieldValue("proceduresId", value);
                    const selected = proceduresData?.items.find(
                      (p) => p.id === value
                    );
                    if (selected) {
                      formik.setFieldValue("value", selected.value)
                      setSelectedProcedure({
                        value: selected.id,
                        label: selected.name,
                      });
                    }
                  }}
                  onBlur={() => formik.handleBlur("proceduresId")}
                  messageError={formik.errors.proceduresId}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label required>{t("LABEL.value")}</Label>
              <div className="space-y-1">
                <MoneyInput
                  placeholder="Valor"
                  isInvalid={formik.touched.value && formik.errors.value}
                  isValid={formik.touched.value && !formik.errors.value}
                  {...formik.getFieldProps("value")}
                  defaultValue={formik.values.value}
                  messageError={formik.errors.value}
                />
              </div>
            </div>

          </div>
          <h3 className="border-t border-app-border-subtle pt-3 font-display text-lg text-app-fg-strong">Data e horário</h3>
          {availableTimesError && (
            <p role="alert" className="text-sm text-app-danger">Não foi possível consultar os horários. Selecione novamente a data ou o profissional para tentar outra consulta.</p>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="scheduling-date" required>{t("LABEL.date")}</Label>
              <BookingDatePicker
                id="scheduling-date"
                {...formik.getFieldProps("date")}
                onChange={(event) => {
                  allowDefaultTimes.current = false;
                  formik.setValues({ ...formik.values, date: event.target.value, period: "", hour: [] });
                }}
                isInvalid={formik.touched.date && !!formik.errors.date}
                isValid={formik.touched.date && !formik.errors.date}
                messageError={formik.errors.date}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="scheduling-period" required>{t("LABEL.period")}</Label>
              <div className="space-y-1">
                <PeriodSelect
                  value={formik.values.period}
                  onValueChange={(value) => {
                    const defaultHours = allowDefaultTimes.current
                      ? validateTimeRange(hours?.[value as keyof TimeSchedulingResponse] ?? [], defaultStartTime, defaultEndTime)
                      : [];
                    allowDefaultTimes.current = false;
                    formik.setValues({ ...formik.values, period: value, hour: defaultHours });
                  }}
                >
                  <SelectTrigger
                    id="scheduling-period"
                    disabled={!hours}
                    loading={
                      formik.values.employeesId !== "" &&
                      formik.values.date !== "" &&
                      availableTimesLoading
                    }
                    isInvalid={
                      formik.touched.period &&
                      formik.errors.period !== undefined
                    }
                    isValid={
                      formik.touched.period &&
                      formik.errors.period === undefined
                    }
                  >
                    <SelectValue placeholder="Selecione o período" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {hours && hours.morning.length > 0 && (
                        <SelectItem value="morning">
                          {t("LABEL.morning")}
                        </SelectItem>
                      )}
                      {hours && hours.afternoon.length > 0 && (
                        <SelectItem value="afternoon">
                          {t("LABEL.afternoon")}
                        </SelectItem>
                      )}
                      {hours && hours.night.length > 0 && (
                        <SelectItem value="night">
                          {t("LABEL.night")}
                        </SelectItem>
                      )}
                    </SelectGroup>
                  </SelectContent>
                </PeriodSelect>
                <MessageError
                  isInvalid={formik.touched.period && formik.errors.period}
                  messageError={formik.errors.period}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label required>{t("LABEL.hour")} (início e fim)</Label>

              <MultiSelect
                options={
                  (hours &&
                    formik.values.period &&
                    hours[
                      formik.values.period as keyof TimeSchedulingResponse
                    ].map((time) => {
                      return {
                        label: removeLastTwoChars(time) ?? "",
                        value: removeLastTwoChars(time) ?? "",
                      };
                    })) ||
                  []
                }
                value={formik.values.hour}
                onChange={(value) => {
                  formik.setFieldValue("hour", [...value as string[]].sort());
                }}
                placeholder="Selecione a hora de início e término"
                multiple={true}
                maxItems={2}
                disabled={!formik.values.period || !hours}
              />
              {invalidRange && <p role="alert" className="text-sm text-app-danger">Selecione horários consecutivos e disponíveis, sem intervalos ocupados.</p>}
              <MessageError isInvalid={formik.touched.hour && !!formik.errors.hour} messageError={typeof formik.errors.hour === "string" ? formik.errors.hour : undefined} />
            </div>

          </div>

          <details className="group border-t border-app-border-subtle pt-3">
            <summary className="min-h-11 cursor-pointer text-sm font-semibold text-app-fg-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
              Confirmação e outras opções
              <span className="mt-1 block text-xs font-normal text-app-fg-muted">{formik.values.queue === "no send" ? "Sem confirmação automática" : "Confirmação automática configurada"} · Retorno, observações e integrações</span>
            </summary>
            <div className="space-y-5 pt-4">
          <div className="rounded-xl border border-app-border p-4">
            <h3 className="text-sm font-semibold text-app-fg-strong">Confirmação automática</h3>
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <div className="space-y-2">
                <Label htmlFor="confirmation-queue">Quando confirmar?</Label>
                <div className="space-y-1">
                  <Select
                    {...formik.getFieldProps("queue")}
                    onValueChange={(value) => {
                      formik.setFieldValue("queue", value);
                    }}
                    defaultValue="no send"
                  >
                    <SelectTrigger
                      id="confirmation-queue"
                      className="w-full sm:w-64"
                    >
                      <SelectValue placeholder="Selecione uma opção" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="no send">
                          Sem confirmação automática
                        </SelectItem>
                        <SelectItem value="send now">
                          Enviar agora
                        </SelectItem>
                        <SelectItem value="24 hours before">
                          1 dia antes
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <MessageError
                    isInvalid={formik.touched.period && formik.errors.period}
                    messageError={formik.errors.period}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 mt-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="whatsapp"
                    checked={formik.values.whatsapp}
                    onCheckedChange={(checked) =>
                      formik.setFieldValue("whatsapp", checked)
                    }
                    disabled={formik.values.queue === "no send"}
                  />
                  <Label htmlFor="whatsapp">WhatsApp</Label>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="scheduling-comments">{t("LABEL.description")}</Label>
              <Textarea
                id="scheduling-comments"
                placeholder="Observações sobre o atendimento"
                {...formik.getFieldProps("comments")}
                isInvalid={formik.touched.comments && formik.errors.comments}
                isValid={formik.touched.comments && !formik.errors.comments}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-5">
              <div className="flex items-center space-x-2">
                <Switch
                  id="return"
                  checked={formik.values.return ? true : false}
                  onCheckedChange={(checked) =>
                    formik.setFieldValue("return", checked ? 1 : 0)
                  }
                />
                <Label htmlFor="return">Retorno</Label>
              </div>

              {/*
                SCRUM-140: Google Agenda is a plan feature. The switch used to
                stay visible and disabled behind
                `hasAccess(user, ["google_calendar"])`, which passes no `canView`
                argument and is therefore false for every clinic. A plan without
                the feature now shows no switch at all; the offer and the way to
                buy it live on Configurações › Integrações.
              */}
              {googleCalendarIncluded ? (
                <div className="flex items-center space-x-2">
                  <Switch
                    id="sync-google"
                    checked={formik.values.syncWithGoogle ? true : false}
                    onCheckedChange={(checked) =>
                      formik.setFieldValue("syncWithGoogle", checked ? 1 : 0)
                    }
                  />
                  <Label htmlFor="sync-google">Sincronizar com Google</Label>
                </div>
              ) : null}

            </div>
          </div>
            </div>
          </details>
        </div>
        <footer className="flex shrink-0 items-center justify-end gap-3 border-t border-app-border bg-app-surface px-5 py-3 sm:px-6">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>Cancelar</Button>
          <Button type="submit" className="bg-petrol text-brand-fg hover:bg-petrol-soft" disabled={isLoading || availableTimesLoading || availableTimesError || invalidRange || !formik.isValid} isLoading={isLoading}>
            Criar agendamento
          </Button>
        </footer>
      </div>
    </form>
  );
}
